import Dexie, { type EntityTable } from 'dexie';

import type { ConflictPayload } from './conflict-types.js';

/**
 * Phase 10 — IndexedDB schema for offline mutation queueing, draft
 * inspections / work orders, and pending photo blobs.
 *
 * Database name is per-app, not per-user. We tag each row with `userId`
 * so we can wipe everything belonging to a specific user on logout
 * without touching anyone else who may have signed in on a shared
 * device.
 */

export type QueueItemStatus =
  | 'pending'
  | 'in_flight'
  | 'succeeded'
  | 'failed'
  | 'conflict';

export interface QueueItem {
  id: string; // uuid
  userId: string;
  orgSlug: string;
  endpoint: string; // request path (e.g. `/api/v1/work-orders`)
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body: unknown;
  idempotencyKey: string;
  /** Stable local id (e.g. for optimistic WO creation). */
  localId?: string | null;
  /** Optional sequencing — items with the same `groupKey` retry in order. */
  groupKey?: string | null;
  /** Photos referenced by this mutation, to be uploaded first. */
  photoIds: string[];
  status: QueueItemStatus;
  attemptCount: number;
  nextAttemptAt: number; // epoch ms
  lastError?: string | null;
  conflict?: ConflictPayload | null;
  createdAt: number;
  updatedAt: number;
}

export interface InspectionDraft {
  id: string; // server inspection id
  userId: string;
  orgSlug: string;
  templateVersion: number;
  answers: Record<string, unknown>;
  updatedAt: number;
}

export interface WorkOrderDraft {
  localId: string; // tmp_<uuid>
  userId: string;
  orgSlug: string;
  buildingId: string;
  unitId?: string | null;
  title: string;
  description?: string | null;
  category: string;
  priority?: string | null;
  photoIds: string[];
  createdAt: number;
}

export interface OfflinePhoto {
  id: string; // uuid
  userId: string;
  orgSlug: string;
  blob: Blob;
  filename: string;
  contentType: string;
  /** Parent entity (e.g. `work_order:tmp_x` or `inspection:abc`). */
  parentKey: string;
  /** Server upload id once uploaded. */
  uploadId?: string | null;
  createdAt: number;
}

export interface OfflineConflictRow {
  id: string; // mirrors queue item id
  userId: string;
  orgSlug: string;
  endpoint: string;
  method: QueueItem['method'];
  conflict: ConflictPayload;
  body: unknown;
  createdAt: number;
}

export class SiteWiseOfflineDb extends Dexie {
  queueItems!: EntityTable<QueueItem, 'id'>;
  inspectionDrafts!: EntityTable<InspectionDraft, 'id'>;
  workOrderDrafts!: EntityTable<WorkOrderDraft, 'localId'>;
  photos!: EntityTable<OfflinePhoto, 'id'>;
  conflicts!: EntityTable<OfflineConflictRow, 'id'>;

  constructor() {
    super('sitewise-offline');
    this.version(1).stores({
      queueItems:
        'id, userId, orgSlug, status, nextAttemptAt, [userId+status], [userId+orgSlug], groupKey, createdAt',
      inspectionDrafts: 'id, userId, [userId+orgSlug], updatedAt',
      workOrderDrafts: 'localId, userId, [userId+orgSlug], createdAt',
      photos: 'id, userId, parentKey, [userId+orgSlug], createdAt',
      conflicts: 'id, userId, [userId+orgSlug], createdAt',
    });
  }
}

let _db: SiteWiseOfflineDb | null = null;

export function getOfflineDb(): SiteWiseOfflineDb {
  if (!_db) _db = new SiteWiseOfflineDb();
  return _db;
}

/**
 * Wipes everything for a given user. Called on logout so the next
 * tenant on a shared device never sees stale photos or queued items.
 */
export async function wipeOfflineDbForUser(userId: string): Promise<void> {
  const db = getOfflineDb();
  await db.transaction(
    'rw',
    [db.queueItems, db.inspectionDrafts, db.workOrderDrafts, db.photos, db.conflicts],
    async () => {
      await db.queueItems.where('userId').equals(userId).delete();
      await db.inspectionDrafts.where('userId').equals(userId).delete();
      await db.workOrderDrafts.where('userId').equals(userId).delete();
      await db.photos.where('userId').equals(userId).delete();
      await db.conflicts.where('userId').equals(userId).delete();
    },
  );
}

/** Test seam — drops & re-opens the entire DB. Never call in prod. */
export async function _resetOfflineDb(): Promise<void> {
  if (_db) {
    await _db.close();
    await Dexie.delete('sitewise-offline');
    _db = null;
  }
}
